import { FC, ReactNode, useEffect, useState } from 'react';

import { tokenStorage } from 'api/client/helpers/tokenStorage';
import { AccessTokenType } from 'api/types/data';
import { LoadingLayoutWrapper } from 'components';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useUser } from 'libs/providers';

interface NavigationMiddlewareChildProps {
  isAuthPage: boolean;
  isLoginPage: boolean;
  isTwoFactorConnectionPage: boolean;
  isUpdateExpiredPasswordPage: boolean;
}

export const NavigationMiddleware: FC<{
  children: (props: NavigationMiddlewareChildProps) => ReactNode;
}> = ({ children }) => {
  const { user } = useUser();
  const { navigate, paths } = useNavigate();

  const [loading, setLoading] = useState(true);
  const isTwoFactorConnectionPage = paths.includes(
    ROUTES.twoFactorConnection.fullPath
  );
  const isUpdateExpiredPasswordPage = paths.includes(
    ROUTES.updateExpiredPassword.fullPath
  );

  const isSetPasswordPage = paths.includes(ROUTES.setPassword.fullPath);

  const isLoginPage =
    paths.includes(ROUTES.login.fullPath) ||
    paths.includes(ROUTES.loginByToken.fullPath);

  const isAuthPage = isLoginPage || isSetPasswordPage;

  const isNeedHomePage =
    isTwoFactorConnectionPage || isAuthPage || isUpdateExpiredPasswordPage;

  const isTempPassword = user?.isPasswordUpdateRequired;

  const hasOtp = !!user?.otp?.verified;

  useEffect(() => {
    if (!user) {
      if (!isAuthPage) {
        navigate(
          ROUTES.login.fullPath,
          {},
          { search: { ret: window.location.href } }
        );
      }
    } else if (
      tokenStorage.getAccessTokenType() === AccessTokenType.ExpiredPassword
    ) {
      navigate(ROUTES.updateExpiredPassword.fullPath, {});
    } else if (isTempPassword) {
      navigate(ROUTES.setPassword.fullPath, {});
    } else if (!hasOtp) {
      navigate(ROUTES.twoFactorConnection.fullPath, {});
    } else if (isNeedHomePage) {
      navigate(ROUTES.fullPath, {});
    }
    setLoading(false);
  }, [isAuthPage, isTempPassword, isNeedHomePage, navigate, user, hasOtp]);

  return (
    <LoadingLayoutWrapper loading={loading}>
      {children({
        isAuthPage,
        isLoginPage,
        isTwoFactorConnectionPage,
        isUpdateExpiredPasswordPage,
      })}
    </LoadingLayoutWrapper>
  );
};
